import type { McpCatalogEntry, McpPlaybookStep, McpStepQuery } from '@/types/mcp'

export type QueryFilter = Record<string, unknown>
export type SortDir = 'asc' | 'desc'
export type QuerySort = { by: string; dir: SortDir }
export type DateChoice = { kind: 'period'; name: string } | { kind: 'asAt'; value: string }
export type FilterOperator = 'equals' | 'notEquals' | 'in' | 'notIn' | 'gt' | 'gte' | 'lt' | 'lte'

/** The API's named periods, which it reads from the model's own Power BI date presets. */
export const PERIOD_LABELS: Record<string, string> = {
  last7Days: 'Last 7 days',
  last30Days: 'Last 30 days',
  thisMonth: 'This month',
  monthToDate: 'Month to date',
  lastMonth: 'Last month',
  last12Months: 'Last 12 months',
  lastMonthLastYear: 'Last month last year',
  thisQuarter: 'This quarter',
  lastQuarter: 'Last quarter',
  thisCalendarYear: 'This calendar year',
  lastCalendarYear: 'Last calendar year',
  thisFinancialYear: 'This financial year',
  financialYearToDate: 'This financial year to date',
  financialYearToLastMonth: 'This financial year to the end of last month',
  lastFinancialYear: 'Last financial year',
  lastFinancialYearToDate: 'Last financial year to date',
  lastFinancialYearToLastMonth: 'Last financial year to the end of last month',
}

const AS_AT_LABELS: Record<string, string> = {
  today: 'As at today',
  endOfLastMonth: 'As at the end of last month',
  endOfLastMonthLastYear: 'As at the end of last month last year',
}

const periods = (...names: string[]): DateChoice[] => names.map((name) => ({ kind: 'period', name }))

/** In the dashboard date picker's group order, with the balance dates (`asAt`) last. */
export const DATE_GROUPS: { label: string; choices: DateChoice[] }[] = [
  { label: 'Days', choices: periods('last7Days', 'last30Days') },
  { label: 'Months', choices: periods('thisMonth', 'monthToDate', 'lastMonth', 'last12Months', 'lastMonthLastYear') },
  { label: 'Quarters', choices: periods('thisQuarter', 'lastQuarter') },
  { label: 'Calendar year', choices: periods('thisCalendarYear', 'lastCalendarYear') },
  {
    label: 'Financial year',
    choices: periods(
      'thisFinancialYear',
      'financialYearToDate',
      'financialYearToLastMonth',
      'lastFinancialYear',
      'lastFinancialYearToDate',
      'lastFinancialYearToLastMonth'
    ),
  },
  {
    label: 'Balances, such as WIP or debtors',
    choices: Object.keys(AS_AT_LABELS).map((value) => ({ kind: 'asAt', value })),
  },
]

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: 'equals',
  notEquals: 'not equals',
  in: 'is one of',
  notIn: 'is not one of',
  gt: 'more than',
  gte: 'at least',
  lt: 'less than',
  lte: 'at most',
}

const COMPARISONS: FilterOperator[] = ['gt', 'gte', 'lt', 'lte']
const COMPARISON_SYMBOLS: Partial<Record<FilterOperator, string>> = { gt: '>', gte: '≥', lt: '<', lte: '≤' }
const NUMERIC_TYPES = new Set(['number', 'currency', 'percent', 'percentage', 'integer', 'decimal', 'hours', 'date'])
// The API refuses these on save: its query would treat them as a plain equals on the first value.
const REFUSED_OPERATORS: Record<string, FilterOperator[]> = {
  boolean: ['notEquals', 'notIn'],
  booleanstring: ['notEquals', 'notIn'],
  number: ['notIn'],
  date: ['in', 'notIn'],
}

const asText = (value: unknown) => (typeof value === 'string' ? value : '')
const asTexts = (value: unknown) => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [])
const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const andList = (items: string[]) =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : (items[0] ?? '')
const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1)

export const queryMeasures = (query: McpStepQuery) => asTexts(query['measures'])
export const queryDimensions = (query: McpStepQuery) => asTexts(query['dimensions'])
export const queryFilters = (query: McpStepQuery): QueryFilter[] =>
  Array.isArray(query['filters']) ? query['filters'].filter(isRecord) : []

export function queryDate(query: McpStepQuery): DateChoice | null {
  const period = asText(query['period'])
  if (period) return { kind: 'period', name: period }
  const asAt = asText(query['asAt'])
  return asAt ? { kind: 'asAt', value: asAt } : null
}

/** The `yyyy-mm-dd` of a balance date picked by hand, not one of the named ones. */
export const asAtDay = (choice: DateChoice | null) =>
  choice?.kind === 'asAt' && /^\d{4}-\d{2}-\d{2}$/.test(choice.value) ? choice.value : null

export function querySort(query: McpStepQuery): QuerySort | null {
  const sort = query['sort']
  if (!isRecord(sort)) return null
  const by = asText(sort['by'])
  if (!by) return null
  return { by, dir: asText(sort['dir']).toLowerCase() === 'asc' ? 'asc' : 'desc' }
}

/** Sets or removes one of query_data's arguments, keeping the rest (top, compareTo, …) as the API sent them. */
export function withArg(query: McpStepQuery, key: string, value: unknown): McpStepQuery {
  const { [key]: _dropped, ...rest } = query
  const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
  return empty ? rest : { ...rest, [key]: value }
}

/** `compareTo` only goes with `asAt`, so it goes when the date stops being a balance date. */
export function withDate(query: McpStepQuery, choice: DateChoice | null): McpStepQuery {
  const next = withArg(withArg(query, 'period', choice?.kind === 'period' ? choice.name : undefined), 'asAt', choice?.kind === 'asAt' ? choice.value : undefined)
  return choice?.kind === 'asAt' ? next : withArg(next, 'compareTo', undefined)
}

/** Drops a sort on a measure or dimension the query no longer has. */
export function withFields(query: McpStepQuery, key: 'measures' | 'dimensions', ids: string[]): McpStepQuery {
  const next = withArg(query, key, ids)
  const sort = querySort(next)
  if (sort && !queryMeasures(next).includes(sort.by) && !queryDimensions(next).includes(sort.by)) {
    return withArg(next, 'sort', undefined)
  }
  return next
}

export const dateChoiceKey = (choice: DateChoice) => (choice.kind === 'period' ? `period:${choice.name}` : `asAt:${choice.value}`)

export function parseDateChoiceKey(key: string): DateChoice | null {
  const [kind, ...rest] = key.split(':')
  const value = rest.join(':')
  if (!value) return null
  if (kind === 'period') return { kind: 'period', name: value }
  if (kind === 'asAt') return { kind: 'asAt', value }
  return null
}

export function dateLabel(choice: DateChoice): string {
  if (choice.kind === 'period') return PERIOD_LABELS[choice.name] ?? choice.name
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(choice.value)
  if (match) return `As at ${match[3]}/${match[2]}/${match[1]}`
  return AS_AT_LABELS[choice.value] ?? `As at ${choice.value}`
}

export type FilterField = { kind: 'dimension' | 'measure'; id: string }

export function filterField(filter: QueryFilter): FilterField | null {
  const dimension = asText(filter['dimension'])
  if (dimension) return { kind: 'dimension', id: dimension }
  const measure = asText(filter['measure'])
  return measure ? { kind: 'measure', id: measure } : null
}

export function operatorsFor(field: FilterField | null, entry: McpCatalogEntry | undefined): FilterOperator[] {
  if (field?.kind === 'measure') return ['gt', 'gte', 'lt', 'lte', 'equals', 'notEquals']
  const type = entry?.type.toLowerCase() ?? ''
  const operators: FilterOperator[] = ['equals', 'notEquals', 'in', 'notIn', ...(NUMERIC_TYPES.has(type) ? COMPARISONS : [])]
  const refused = REFUSED_OPERATORS[type] ?? []
  return operators.filter((operator) => !refused.includes(operator))
}

export const isListOperator = (operator: unknown) => operator === 'in' || operator === 'notIn'

/**
 * A typed number is sent as a number; anything else (including an input such as `{client}`) as text.
 * Only when it reads back the same, so "1.0" or "-0" stay text while they're being typed.
 */
export function parseFilterValue(text: string): string | number {
  const trimmed = text.trim()
  const number = Number(trimmed)
  return /^-?\d+(\.\d+)?$/.test(trimmed) && String(number) === trimmed ? number : text
}

const filterValues = (filter: QueryFilter) =>
  (Array.isArray(filter['value']) ? filter['value'] : [filter['value']])
    .filter((value) => value !== undefined && value !== null && value !== '')
    .map(String)

// The API reads a measure filter's value as a number ("1,000" and "1e3" too, but not "0x10") and keeps no rows when it can't.
const isNumberOrInput = (value: string) =>
  /^\{[^{}]+\}$/.test(value.trim()) || /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(value.trim().replace(/,/g, ''))

// The API reads operators ignoring case, so a playbook saved through your AI assistant may spell them differently.
const lowered = (operators: string[]) => operators.map((operator) => operator.toLowerCase())
const ALL_OPERATORS = lowered(Object.keys(OPERATOR_LABELS))
const MEASURE_OPERATORS = lowered(operatorsFor({ kind: 'measure', id: '' }, undefined))
const LIST_OPERATORS = lowered(['in', 'notIn'])

/** What's wrong with a step's query, in the order to fix it; empty when it's ready to save. */
export function queryProblems(query: McpStepQuery, entries?: Map<string, McpCatalogEntry>): string[] {
  const measures = queryMeasures(query)
  const problems = measures.length ? [] : ['Choose at least one measure.']
  const sections = new Set(measures.map((id) => entries?.get(id)?.section.toLowerCase()).filter(Boolean))
  if (sections.size > 1) problems.push('These measures come from different sections. Use a step for each.')
  if (asText(query['period']) && asText(query['asAt'])) {
    problems.push('This step has both a date range and a balance date, and the form only shows the range. Remove the date filter and choose one.')
  }
  for (const filter of queryFilters(query)) {
    const field = filterField(filter)
    const fromStep = Boolean(asText(filter['valuesFrom']))
    const operator = asText(filter['operator']).toLowerCase()
    if (!field) {
      problems.push('Choose what to filter on, or remove the filter.')
    } else if (asText(filter['dimension']) && asText(filter['measure'])) {
      problems.push('Filter on a dimension or a measure, not both.')
    } else if (field.kind === 'measure' && fromStep) {
      problems.push('Only a dimension’s values can be taken from a step.')
    } else if (field.kind === 'measure' && !measures.includes(field.id)) {
      problems.push('A filter on a measure needs that measure in Measures.')
    } else if (fromStep ? Boolean(operator) && !LIST_OPERATORS.includes(operator) : !(field.kind === 'measure' ? MEASURE_OPERATORS : ALL_OPERATORS).includes(operator)) {
      problems.push('Choose how each filter compares, or remove it.')
    } else if (!fromStep && filterValues(filter).length === 0) {
      problems.push('Give each filter a value, or remove it.')
    } else if (field.kind === 'measure' && !filterValues(filter).every(isNumberOrInput)) {
      problems.push('A filter on a measure needs a number, or an input such as {amount}.')
    }
  }
  return [...new Set(problems)]
}

function filterPhrase(filter: QueryFilter, steps: McpPlaybookStep[], label: (id: string) => string): string {
  const field = filterField(filter)
  if (!field) return ''
  const from = asText(filter['valuesFrom'])
  if (from) {
    const number = steps.findIndex((step) => step.id === from) + 1
    const source = number ? `step ${number}` : `the “${from}” step`
    return `${filter['operator'] === 'notIn' ? 'not in' : 'in'} ${source}’s ${label(field.id)}s`
  }
  const values = filterValues(filter)
  if (values.length === 0) return ''
  const operator = asText(filter['operator']) as FilterOperator
  if (operator === 'equals' || operator === 'in') return `for ${values.join(', ')}`
  if (operator === 'notEquals' || operator === 'notIn') return `excluding ${values.join(', ')}`
  const symbol = COMPARISON_SYMBOLS[operator]
  return symbol ? `where ${label(field.id)} ${symbol} ${values[0]}` : ''
}

/**
 * The title a Get data step starts with, from its settings, e.g. "WIP by Job for {client}, biggest first".
 * Empty until a measure is chosen.
 */
export function defaultTitle(query: McpStepQuery, steps: McpPlaybookStep[], entries: Map<string, McpCatalogEntry>): string {
  const label = (id: string) => entries.get(id)?.label ?? id
  const measures = queryMeasures(query)
  if (measures.length === 0) return ''
  const dimensions = queryDimensions(query)
  const what = dimensions.length
    ? `${andList(measures.map(label))} by ${andList(dimensions.map(label))}`
    : andList(measures.map(label))
  const head = [what, ...queryFilters(query).map((filter) => filterPhrase(filter, steps, label))].filter(Boolean).join(' ')

  const date = queryDate(query)
  const sort = querySort(query)
  let order = ''
  if (sort) {
    const onMeasure = measures.includes(sort.by)
    const direction = onMeasure ? (sort.dir === 'desc' ? 'biggest first' : 'smallest first') : sort.dir === 'asc' ? 'A to Z' : 'Z to A'
    order = sort.by === measures[0] ? direction : `${label(sort.by)} ${direction}`
  }
  return [head, date ? lowerFirst(dateLabel(date)) : '', order].filter(Boolean).join(', ')
}

const STEP_ID_MAX = 40

/** An id for a step a later step takes values from: the API's `^[a-z][a-z0-9_]{0,39}$`, unique in the playbook. */
export function stepIdFrom(text: string, taken: Iterable<string>): string {
  const used = new Set(taken)
  const slug = text
    .toLowerCase()
    .replace(/\{[^}]*\}/g, ' ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  const prefix = /^[a-z]/.test(slug) ? '' : slug ? 'step_' : 'step'
  const withEnding = (ending: string) => {
    const room = STEP_ID_MAX - prefix.length - ending.length
    return `${prefix}${slug.slice(0, room).replace(/_+$/, '')}${ending}`.replace(/_+$/, '')
  }
  const first = withEnding('')
  if (!used.has(first)) return first
  let suffix = 2
  while (used.has(withEnding(`_${suffix}`))) suffix += 1
  return withEnding(`_${suffix}`)
}

export type BrokenReference = { step: number; source: number | null }

/** Steps (numbered from 1) that take values from a step that isn't above them. */
export function brokenReferences(steps: McpPlaybookStep[]): BrokenReference[] {
  return steps.flatMap((step, index) =>
    queryFilters(step.query ?? {}).flatMap((filter) => {
      const from = asText(filter['valuesFrom'])
      if (!from) return []
      const source = steps.findIndex((other) => other.id === from)
      return source >= 0 && source < index ? [] : [{ step: index + 1, source: source >= 0 ? source + 1 : null }]
    })
  )
}

/** Why the API would refuse step `index`'s links to earlier steps; empty when they're fine. */
export function linkProblems(steps: McpPlaybookStep[], index: number, label: (id: string) => string = (id) => id): string[] {
  const problems = queryFilters(steps[index]?.query ?? {}).flatMap((filter) => {
    const from = asText(filter['valuesFrom'])
    if (!from) return []
    const source = steps.findIndex((other) => other.id === from)
    const sourceQuery = steps[source]?.query
    if (source < 0 || !sourceQuery) return ['This step takes values from a step that was removed or switched to Instruction.']
    if (source >= index) return [`This step takes values from step ${source + 1}, which isn’t above it.`]
    const field = filterField(filter)
    if (field && !queryDimensions(sourceQuery).some((id) => id.toLowerCase() === field.id.toLowerCase())) {
      return [`This step takes ${label(field.id)} from step ${source + 1}, which isn’t grouped by it.`]
    }
    return []
  })
  return [...new Set(problems)]
}

/** The numbers of the steps whose filters take values from step `id`'s rows. */
export function stepsUsing(steps: McpPlaybookStep[], id: string): number[] {
  return steps.flatMap((step, index) =>
    queryFilters(step.query ?? {}).some((filter) => filter['valuesFrom'] === id) ? [index + 1] : []
  )
}

const OPERATOR_SPELLINGS = new Map(Object.keys(OPERATOR_LABELS).map((op) => [op.toLowerCase(), op]))

/**
 * The API reads operators and step links ignoring case, so one saved by the AI assistant as "NotIn" or
 * "Jobs" works there. The form compares them exactly, so they're put into its spelling when it loads.
 */
export function canonicalSteps(steps: McpPlaybookStep[]): McpPlaybookStep[] {
  return steps.map((step) => {
    const filters = step.query?.['filters']
    if (!step.query || !Array.isArray(filters)) return step
    return {
      ...step,
      query: {
        ...step.query,
        filters: filters.map((filter: unknown) => {
          if (!isRecord(filter)) return filter
          const operator = OPERATOR_SPELLINGS.get(asText(filter['operator']).toLowerCase())
          const from = asText(filter['valuesFrom'])
          return { ...filter, ...(operator ? { operator } : {}), ...(from ? { valuesFrom: from.toLowerCase() } : {}) }
        }),
      },
    }
  })
}

export function moveItem<T>(items: T[], from: number, to: number): T[] {
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved as T)
  return next
}
